<template>
  <div :class="$style.profileImageSection" :data-mode="mode">
    <div :class="$style.profileImageContainer" :data-size="size" @click="handleClick"
      :style="{ cursor: showControls && !loading ? 'pointer' : 'default' }">
      <!-- 로딩 상태일 때 스켈레톤 UI -->
      <div v-if="loading" :class="$style.profileImageSkeleton"></div>

      <!-- 로딩 완료 후 실제 이미지 -->
      <template v-else>
        <img v-if="displayImageUrl" :src="displayImageUrl" :alt="artistName" :class="$style.profileImage" />
        <img v-else-if="defaultImageByType" :src="defaultImageByType" :alt="`${artistType} 기본 이미지`"
          :class="[$style.profileImage, $style.defaultImage]" />
        <div v-else :class="$style.noProfileImage">
          <img :src="require('@/assets/images/default_profile.png')" alt="기본 프로필" :class="$style.profileImage" />
        </div>

        <!-- 편집 아이콘 오버레이 -->
        <div v-if="showControls" :class="$style.editOverlay">
          <span :class="$style.editIcon">✏️</span>
        </div>
      </template>
    </div>

    <!-- 프로필 이미지 편집 모달 -->
    <ProfileImageModal :is-visible="isModalVisible" :current-image="userProfileImage" :display-name="artistName"
      :mode="mode" :user-email="userEmail" @close="closeModal" @image-uploaded="handleImageUpdated"
      @image-removed="handleImageRemoved" />
  </div>
</template>

<script>
import ProfileImageModal from "./modal/ProfileImageModal.vue";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { storage } from "/firebaseConfig";
import { getArtistTypeIconKey } from "@/utils/artistTypes";

export default {
  name: "ProfileImageUploader",
  components: {
    ProfileImageModal,
  },
  props: {
    artistName: {
      type: String,
      required: true,
    },
    profileImage: {
      type: String,
      default: null,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: "80px", // 기본 크기
    },
    mode: {
      type: String,
      default: "update-artist", // "user", "new-artist", 또는 "update-artist"
      validator: (value) =>
        ["user", "new-artist", "update-artist"].includes(value),
    },
    userEmail: {
      type: String,
      default: null, // user 모드일 때 사용
    },
    loading: {
      type: Boolean,
      default: false, // 로딩 상태
    },
    artistType: {
      type: [String, Array],
      default: null, // 아티스트 타입 (보컬, 밴드, 믹스 등) - 문자열 또는 배열
    },
  },
  emits: ["image-uploaded", "image-deleted"],
  data() {
    return {
      isModalVisible: false,
      currentImageUrl: null, // 내부적으로 관리하는 이미지 URL
      generatedImageUrl: null, // 파일명에서 생성된 URL
    };
  },
  computed: {
    defaultImageByType() {
      const isArtistMode =
        this.mode === "new-artist" ||
        this.mode === "update-artist";
      if (!isArtistMode || !this.artistType) {
        return null;
      }

      const iconKey = getArtistTypeIconKey(this.artistType);
      try {
        return require(`@/assets/images/${iconKey}.png`);
      } catch {
        return require("@/assets/images/default_profile.png");
      }
    },
    // 실제 표시할 이미지 URL
    displayImageUrl() {
      // 우선순위:
      // 1. currentImageUrl (모달에서 방금 업로드한 이미지)
      // 2. prop이 URL인 경우 prop (부모에서 업데이트된 최신 값)
      // 3. generatedImageUrl (파일명으로부터 생성된 URL)
      // 4. profileImage prop (기본값)

      // currentImageUrl이 있으면 우선 사용 (업로드 직후)
      if (this.currentImageUrl) {
        return this.currentImageUrl;
      }

      // prop이 URL이면 우선 사용 (부모에서 업데이트된 값)
      if (this.profileImage && this.profileImage.startsWith("http")) {
        return this.profileImage;
      }

      // generatedImageUrl 사용
      if (this.generatedImageUrl) {
        return this.generatedImageUrl;
      }

      // 삭제된 경우 (모두 null)
      if (this.currentImageUrl === null && !this.profileImage) {
        return null;
      }

      // 기본값으로 profileImage prop 사용
      return this.profileImage;
    },
    // 모달에 전달할 실제 사용자 이미지 (기본 이미지 제외)
    userProfileImage() {
      return (
        this.currentImageUrl ||
        this.generatedImageUrl ||
        (this.profileImage && !this.profileImage.includes("default_profile.png")
          ? this.profileImage
          : null)
      );
    },
  },
  watch: {
    profileImage(newVal, oldVal) {
      // prop이 변경되면 URL 생성
      this.generateImageUrl(newVal);
      // prop이 실제로 변경된 경우 currentImageUrl 초기화
      // (prop 변경이 반영되도록 하기 위함)
      if (newVal !== oldVal) {
        // prop이 새로운 URL이면 currentImageUrl 초기화하여 prop 변경 반영
        // 단, currentImageUrl과 같은 값이면 초기화하지 않음 (업로드 직후인 경우)
        if (newVal !== this.currentImageUrl) {
          this.currentImageUrl = null;
        }
      }
    },
  },

  async mounted() {
    // 컴포넌트 마운트 시 초기 URL 생성
    if (this.profileImage) {
      await this.generateImageUrl(this.profileImage);
    }
  },
  methods: {
    handleClick(event) {
      // 이벤트 전파 중단 (부모 요소의 클릭 이벤트 방지)
      if (event) {
        event.stopPropagation();
      }
      if (!this.loading && this.showControls) {
        this.openModal();
      }
    },

    openModal() {
      if (this.showControls) {
        this.isModalVisible = true;
      }
    },

    closeModal() {
      this.isModalVisible = false;
    },

    handleImageUpdated(imageData) {
      // 이미지 업로드 완료 시 화면에 바로 반영
      // imageData가 객체인 경우 (url, fileName) 또는 문자열인 경우 (url)
      const url = typeof imageData === "object" ? imageData.url : imageData;

      this.currentImageUrl = url; // 내부 상태 업데이트
      // 삭제된 경우 (null) generatedImageUrl도 초기화
      if (url === null || url === undefined) {
        this.generatedImageUrl = null;
      }
      // 화면 업데이트를 위한 강제 렌더링
      this.$nextTick(() => {
        this.$forceUpdate();
      });
      this.$emit("image-uploaded", imageData);
    },

    handleImageRemoved() {
      this.currentImageUrl = null; // 내부 상태 초기화
      this.generatedImageUrl = null;
      this.$emit("image-deleted");

      // 강제로 화면 업데이트를 위해 nextTick 사용
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    async generateImageUrl(imageData) {
      if (!imageData) {
        this.generatedImageUrl = null;
        return;
      }

      // 이미 URL인 경우
      if (imageData.startsWith("http")) {
        this.generatedImageUrl = imageData;
        return;
      }

      // 파일명인 경우 Firebase Storage URL 생성
      try {
        // mode에 따라 경로 결정 (user 모드만 profile, 나머지는 모두 artist)
        const storagePath = this.mode === "user" ? "profile" : "artist";
        // 파일명만 사용 (경로는 포함되지 않음)
        const fileName = imageData;
        const imagePath = `${storagePath}/${fileName}`;

        const imageRef = storageRef(storage, imagePath);
        const url = await getDownloadURL(imageRef);
        this.generatedImageUrl = url;
      } catch (error) {
        console.warn("이미지 URL 생성 실패:", error);
        this.generatedImageUrl = null;
      }
    },
  },
};
</script>

<style module>
.profileImageSection {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 12px;
  border-radius: 6px;
}

/* 사용자 프로필용 스타일 (MyProfile에서 사용) */
.profileImageSection[data-mode="user"] {
  background: transparent;
  border: none;
  padding: 0;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
}

.profileImageContainer {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease;
}

.profileImageContainer:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* 동적 크기 적용 */
.profileImageContainer[data-size="120px"] {
  width: 120px;
  height: 120px;
  border: 3px solid #e9ecef;
}

.profileImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.defaultImage {
  opacity: 0.8;
  filter: brightness(0.9);
}

.noProfileImage {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
}

/* 편집 오버레이 */
.editOverlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 50%;
  z-index: 2;
}

.profileImageContainer:hover .editOverlay {
  opacity: 1;
}

.editIcon {
  font-size: 28px;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

/* 스켈레톤 UI */
.profileImageSkeleton {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

/* ========================================
   반응형 디자인
   - Large: 1025px ~ (기본 스타일)
   - Medium: 481px ~ 1024px
   - Small: ~ 480px
   ======================================== */

/* Medium (481px ~ 1024px) */
@media (max-width: 1024px) {
  .profileImageSection {
    gap: 12px;
    padding: 10px;
  }

  .profileImageSection[data-mode="user"] {
    gap: 8px;
  }

  .profileImageContainer {
    width: 70px;
    height: 70px;
  }

  .profileImageContainer[data-size="120px"] {
    width: 100px;
    height: 100px;
  }

  .profileImageSection[data-mode="update-artist"],
  .profileImageSection[data-mode="new-artist"] {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .editIcon {
    font-size: 24px;
  }
}

/* Small (~ 480px) */
@media (max-width: 480px) {
  .profileImageSection {
    gap: 10px;
    padding: 8px;
    border-radius: 4px;
  }

  .profileImageSection[data-mode="user"] {
    gap: 6px;
  }

  .profileImageContainer {
    width: 60px;
    height: 60px;
    border-width: 1.5px;
  }

  .profileImageContainer[data-size="120px"] {
    width: 80px;
    height: 80px;
    border-width: 2px;
  }

  .profileImageContainer:hover {
    transform: scale(1.03);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  }

  .editOverlay {
    background: rgba(0, 0, 0, 0.5);
  }

  .editIcon {
    font-size: 18px;
  }
}
</style>
